import { useCallback } from 'react';
import { useHistory, useLocation } from 'umi';
import { useBoolean } from 'ahooks';
import { isEmpty } from 'lodash-es';
import { DATABRAIN_AGENT_ID2 } from '@/pages/aigc/pages/Agent/config/constant';
import { useCopilotOpenBridge } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
// import { setCopilotDefaultWindowMode } from '@/pages/aigc/pages/Agent/Copilot/utils/copilotDefaultWindowMode';
import { useAgentStoreContext } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { buriedBtnPoint } from '@home/utils/buriedPoint';

/**
 * Layer 2 业务逻辑 Hook
 * 封装 AgentTipBubble 的业务规则与状态管理：
 * 1. 全屏模式 / Copilot 模式入口跳转
 * 2. 处理「前往 Brainy 使用 GPT」点击逻辑（埋点 + 设置 Agent + 跳转）
 * 3. 处理「反馈问题」点击逻辑（埋点 + 控制弹窗显示）
 */
export function useAgentTipBubbleLogic() {
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const isAgentPage = pathname?.split('/')[1] === 'agent';
  const agentQuery = (location as { query?: Record<string, string>; }).query || {};
  const { getItem: agentGetItem, setItem: agentSetItem } = useAgentStoreContext();
  const sessionCopilotId = agentGetItem('__dba_chat_copilot__', '');
  const conversations = agentGetItem(`__dba_chat_history_${sessionCopilotId}__`);
  const { openCopilotWithAgentId, showCopilot } = useCopilotOpenBridge();
  const [feedbackVisible, { setTrue: openFeedback, setFalse: closeFeedback }] = useBoolean(false);

  /** 全屏模式：跳转 Agent 全屏页（与点击 AI Icon 一致） */
  const handleNavigateToAgent = useCallback(() => {
    buriedBtnPoint({
      buttonId: '100101014',
      extInfo: 'jump to agent',
      pageId: '100101',
      typeId: 'homePage_pc_v2',
    });

    if (!isEmpty(conversations) && !isAgentPage) {
      history.push(`/agent/chat?sessionId=${sessionCopilotId}`);
    } else {
      history.push('/agent/home');
    }
  }, [conversations, history, isAgentPage, sessionCopilotId]);

  /** Copilot 模式：默认固定 + 打开 Copilot；在 Agent 页时同步 session 并回首页 */
  const handleCopilotMode = useCallback(() => {
    buriedBtnPoint({
      buttonId: '100101016',
      extInfo: 'open copilot docked from nav ai icon',
      pageId: '100101',
      typeId: 'homePage_pc_v2',
    });
    // 注：不在此处修改 localStorage 偏好，偏好变更仅限 Copilot 更多菜单 → 默认窗口 Switch
    if (isAgentPage) {
      const { sessionId: agentSessionId } = agentQuery;
      if (agentSessionId) {
        agentSetItem('__dba_chat_copilot__', agentSessionId);
      }
      showCopilot();
      history.push({ pathname: '/home' });
      return;
    }

    showCopilot();
  }, [agentQuery, agentSetItem, history, isAgentPage, showCopilot]);

  /** 点击「前往 Brainy 使用 GPT」：埋点 + 设置 Agent + 跳转 /agent/home */
  const handleGoToGPT = useCallback(() => {
    buriedBtnPoint({
      buttonId: '70090106',
      extInfo: JSON.stringify({ action: 'go_to_gpt' }),
      pageId: '700501',
      typeId: 'homePage_pc_v2',
    });

    // 1. 传入优先级 ID 数组，内部自行解析并写入 localStorage
    openCopilotWithAgentId(['41', '52', DATABRAIN_AGENT_ID2]);
    // 2. 外部负责跳转
    history.push('/agent/home');
  }, [history, openCopilotWithAgentId]);

  /** 点击「反馈问题」：埋点 + 显示反馈弹窗 */
  const handleOpenFeedback = useCallback(() => {
    buriedBtnPoint({
      buttonId: '70090107',
      extInfo: JSON.stringify({ action: 'open_feedback' }),
      pageId: '700501',
      typeId: 'homePage_pc_v2',
    });
    openFeedback();
  }, [openFeedback]);

  /** 关闭反馈弹窗 */
  const handleCloseFeedback = useCallback(() => {
    closeFeedback();
  }, [closeFeedback]);

  return {
    /** 业务状态：反馈弹窗是否可见 */
    feedbackVisible,
    /** 业务操作：全屏模式 / 点击 AI Icon */
    handleNavigateToAgent,
    /** 业务操作：Copilot 模式 */
    handleCopilotMode,
    /** 业务操作：前往 Brainy 使用 GPT */
    handleGoToGPT,
    /** 业务操作：打开反馈弹窗 */
    handleOpenFeedback,
    /** 业务操作：关闭反馈弹窗 */
    handleCloseFeedback,
  };
}
